import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, WifiOff, Zap, Smartphone, CheckCircle2, Share } from 'lucide-react';
import { downloadCatalog, dismissOfflinePrompt, getOfflineInfo } from '../../lib/offlineCatalog';

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const InstallSection = ({ t }) => {
    const [prompt, setPrompt] = useState(window.__kjInstallPrompt || null);
    useEffect(() => {
        const h = () => setPrompt(window.__kjInstallPrompt || null);
        window.addEventListener('kj-install-available', h);
        return () => window.removeEventListener('kj-install-available', h);
    }, []);
    if (isStandalone()) return null;
    const install = async () => {
        prompt.prompt();
        await prompt.userChoice.catch(() => {});
        window.__kjInstallPrompt = null;
        setPrompt(null);
    };
    if (!prompt && !isIOS()) return null;
    return (
        <div data-testid="offline-install-section" className="mt-4 p-3 rounded-xl flex items-center gap-3" style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}` }}>
            <Smartphone className="w-6 h-6 flex-shrink-0" style={{ color: t.colors.accent }} />
            {prompt ? (
                <>
                    <p className="text-xs flex-1" style={{ color: t.colors.textMuted }}>Instala la app en tu celular para abrirla como una app normal.</p>
                    <button data-testid="offline-install-btn" onClick={install} className="px-3 py-2 rounded-full text-xs font-bold" style={{ background: t.colors.text, color: t.colors.bg }}>Instalar</button>
                </>
            ) : (
                <p data-testid="offline-install-ios-hint" className="text-xs flex-1" style={{ color: t.colors.textMuted }}>
                    Para instalar la app: toca <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <b>Compartir</b> y luego <b>"Agregar a pantalla de inicio"</b>.
                </p>
            )}
        </div>
    );
};

export const CatalogoOfflineModal = ({ open, onClose, t }) => {
    const [state, setState] = useState('intro');
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [result, setResult] = useState(null);
    const prev = getOfflineInfo();

    useEffect(() => { if (open && state === 'done') setState('intro'); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async () => {
        setState('downloading');
        const info = await downloadCatalog(setProgress);
        setResult(info);
        setState('done');
    };
    const close = () => {
        if (state === 'downloading') return;
        if (state === 'intro' && !prev) dismissOfflinePrompt();
        onClose();
    };
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}
                    className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
                    <motion.div data-testid="offline-catalog-modal" onClick={(e) => e.stopPropagation()}
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ type: 'spring', damping: 26, stiffness: 300 }}
                        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-8" style={{ background: t.colors.bg, color: t.colors.text, border: `1px solid ${t.colors.border}` }}>
                        {state !== 'downloading' && (
                            <button data-testid="offline-modal-close-btn" onClick={close} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: t.colors.surface }}>
                                <X className="w-4 h-4" />
                            </button>
                        )}
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: t.colors.accent, color: t.colors.badgeText || t.colors.bg }}>
                            {state === 'done' ? <CheckCircle2 className="w-7 h-7" /> : <Download className="w-7 h-7" />}
                        </div>

                        {state === 'intro' && (
                            <>
                                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: t.font }}>{prev ? 'Actualizar catálogo' : '¿Descargar el catálogo?'}</h2>
                                <p className="text-sm mb-4" style={{ color: t.colors.textMuted }}>
                                    Guarda todos los productos y fotos en tu celular. Así el catálogo abre al instante, aunque tu internet esté lento o sin señal.
                                </p>
                                <div className="space-y-2 mb-5">
                                    <div className="flex items-center gap-2 text-sm"><Zap className="w-4 h-4" style={{ color: t.colors.accent }} /> Navegación mucho más rápida</div>
                                    <div className="flex items-center gap-2 text-sm"><WifiOff className="w-4 h-4" style={{ color: t.colors.accent }} /> Ver productos sin conexión</div>
                                </div>
                                {prev && <p data-testid="offline-last-download" className="text-xs mb-3" style={{ color: t.colors.textMuted }}>Última descarga: {new Date(prev.at).toLocaleString('es-MX')}</p>}
                                <button data-testid="offline-download-btn" onClick={start} className="w-full py-3.5 rounded-full font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" style={{ background: t.colors.accent, color: t.colors.badgeText || t.colors.bg }}>
                                    <Download className="w-4 h-4" /> {prev ? 'Actualizar ahora' : 'Descargar catálogo'}
                                </button>
                                <button data-testid="offline-later-btn" onClick={close} className="w-full py-3 mt-2 text-sm" style={{ color: t.colors.textMuted }}>Ahora no</button>
                                <InstallSection t={t} />
                            </>
                        )}

                        {state === 'downloading' && (
                            <div data-testid="offline-downloading">
                                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: t.font }}>Descargando catálogo…</h2>
                                <p className="text-sm mb-5" style={{ color: t.colors.textMuted }}>No cierres esta página. Esto puede tardar un poco.</p>
                                <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: t.colors.surface }}>
                                    <motion.div className="h-full rounded-full" style={{ background: t.colors.accent }} animate={{ width: `${pct}%` }} />
                                </div>
                                <p data-testid="offline-progress-text" className="text-xs font-mono" style={{ color: t.colors.textMuted }}>{pct}% · {progress.done}/{progress.total}</p>
                            </div>
                        )}

                        {state === 'done' && result && (
                            <div data-testid="offline-done">
                                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: t.font }}>¡Catálogo listo!</h2>
                                <p data-testid="offline-done-summary" className="text-sm mb-5" style={{ color: t.colors.textMuted }}>
                                    Se guardaron {result.productos} productos y {result.fotos} fotos. Ya puedes ver el catálogo aunque no tengas internet.
                                    {result.failed > 0 && ` (${result.failed} archivos no se pudieron descargar.)`}
                                </p>
                                <button data-testid="offline-done-btn" onClick={onClose} className="w-full py-3.5 rounded-full font-bold" style={{ background: t.colors.accent, color: t.colors.badgeText || t.colors.bg }}>Ver catálogo</button>
                                <InstallSection t={t} />
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
